// /reports/monthly-tracking — replicates the legacy "Monthly Tracking" tab.
// Pivot: rows = FRT Customer, columns = months × {accts, balance}.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { reports } from '@/lib/api';
import { formatBalance } from '@/lib/format';
import type { MonthlyTrackingCustomerRow } from '@frt/shared-types';

type SortKey = 'frt_customer_status' | 'frt_customer_code';

const fmtCurrency = formatBalance;

function defaultRange(): { from: string; to: string } {
  const d = new Date();
  const yyyy = d.getUTCFullYear();
  const mm = d.getUTCMonth() + 1; // 1..12
  const fromY = mm >= 6 ? yyyy : yyyy - 1;
  const fromM = mm >= 6 ? mm - 5 : mm + 7;
  return {
    from: `${fromY}-${String(fromM).padStart(2, '0')}`,
    to: `${yyyy}-${String(mm).padStart(2, '0')}`,
  };
}

export function MonthlyTrackingPage() {
  const initial = defaultRange();
  const [from, setFrom] = useState<string>(initial.from);
  const [to, setTo] = useState<string>(initial.to);
  const [statusFilter, setStatusFilter] = useState<'' | 'active' | 'suppressed'>('');
  const [customerFilter, setCustomerFilter] = useState<string>('');
  const [sort, setSort] = useState<SortKey>('frt_customer_code');
  const [dir, setDir] = useState<'asc' | 'desc'>('asc');

  const q = useQuery({
    queryKey: ['reports', 'monthly-tracking', from, to],
    queryFn: () => reports.monthlyTracking(from, to),
    enabled: Boolean(from) && Boolean(to),
  });

  const onSort = (key: SortKey) => {
    if (key === sort) {
      setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(key);
      setDir('asc');
    }
  };

  // Filter + sort the customer rows client-side. Totals row is recomputed
  // from the visible subset so the table footer reflects the filtered view.
  const visibleRows = useMemo<MonthlyTrackingCustomerRow[]>(() => {
    if (!q.data) return [];
    const term = customerFilter.trim().toLowerCase();
    let rows = q.data.customers.filter((c) => {
      if (statusFilter && c.frt_customer_status !== statusFilter) return false;
      if (term) {
        const hay = `${c.frt_customer_code} ${c.frt_customer_name}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
    const mul = dir === 'asc' ? 1 : -1;
    rows = [...rows].sort((a, b) => {
      const av = a[sort] ?? '';
      const bv = b[sort] ?? '';
      return String(av).localeCompare(String(bv)) * mul;
    });
    return rows;
  }, [q.data, statusFilter, customerFilter, sort, dir]);

  const visibleTotals = useMemo(() => {
    if (!q.data) return null;
    const t: Record<string, { account_count: number; total_balance: number }> = {};
    for (const p of q.data.periods) {
      t[p] = { account_count: 0, total_balance: 0 };
    }
    for (const c of visibleRows) {
      for (const p of q.data.periods) {
        const cell = c.by_period[p];
        if (!cell) continue;
        t[p]!.account_count += cell.account_count;
        t[p]!.total_balance += cell.total_balance;
      }
    }
    return t;
  }, [q.data, visibleRows]);

  const onExport = useMemo(
    () => async (format: 'xlsx' | 'csv') => {
      try {
        await reports.monthlyTrackingExport(from, to, format);
      } catch (err) {
        console.error(err);
      }
    },
    [from, to],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-semibold text-slate-900">Monthly Tracking</h1>
        <div className="flex items-center gap-2 text-sm">
          <label>
            From{' '}
            <input
              type="month"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="rounded border border-slate-300 px-2 py-1"
            />
          </label>
          <label>
            To{' '}
            <input
              type="month"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="rounded border border-slate-300 px-2 py-1"
            />
          </label>
          <Button size="sm" variant="secondary" onClick={() => onExport('xlsx')}>
            Export XLSX
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onExport('csv')}>
            Export CSV
          </Button>
        </div>
      </div>

      <Card>
        <CardBody className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
          <label className="block">
            <span className="block text-slate-600">Status</span>
            <select
              className="w-full rounded border border-slate-300 bg-white px-2 py-1.5"
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as '' | 'active' | 'suppressed')
              }
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="suppressed">Suppressed</option>
            </select>
          </label>
          <label className="block sm:col-span-2">
            <span className="block text-slate-600">Customer (search code or name)</span>
            <Input
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              placeholder="e.g. RF, Ally, …"
            />
          </label>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>By FRT Customer</CardTitle>
        </CardHeader>
        <CardBody className="overflow-auto">
          {q.isLoading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : q.error ? (
            <div className="text-sm text-red-600">{(q.error as Error).message}</div>
          ) : q.data ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wide text-slate-500">
                  <SortableTh
                    label="Status"
                    field="frt_customer_status"
                    sort={sort}
                    dir={dir}
                    onSort={onSort}
                    rowSpan={2}
                  />
                  <SortableTh
                    label="FRT Customer"
                    field="frt_customer_code"
                    sort={sort}
                    dir={dir}
                    onSort={onSort}
                    rowSpan={2}
                  />
                  {q.data.periods.map((p) => (
                    <th key={p} colSpan={2} className="py-1 px-2 text-center border-l border-slate-200">
                      {p}
                    </th>
                  ))}
                </tr>
                <tr className="text-xs text-slate-400">
                  {q.data.periods.map((p) => (
                    <FromOnePeriodHeaders key={p} />
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visibleRows.map((cust) => (
                  <tr key={`${cust.frt_customer_code}-${cust.frt_customer_id}`}>
                    <td className="py-1 pl-1">
                      <StatusBadge status={cust.frt_customer_status} />
                    </td>
                    <td className="py-1 pl-1">
                      <span className="font-medium">{cust.frt_customer_code}</span>
                      <span className="ml-2 text-slate-500">{cust.frt_customer_name}</span>
                    </td>
                    {q.data.periods.map((p) => {
                      const cell = cust.by_period[p];
                      return (
                        <FromOnePeriodCells
                          key={p}
                          accts={cell?.account_count ?? 0}
                          bal={cell?.total_balance ?? 0}
                        />
                      );
                    })}
                  </tr>
                ))}
                {visibleRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={2 + q.data.periods.length * 2}
                      className="py-4 text-center text-slate-500"
                    >
                      No customers match the current filters.
                    </td>
                  </tr>
                )}
                <tr className="border-t-2 border-slate-300 font-semibold">
                  <td className="py-1 pl-1" />
                  <td className="py-1 pl-1">
                    Total{statusFilter || customerFilter ? ' (filtered)' : ''}
                  </td>
                  {q.data.periods.map((p) => {
                    const cell = visibleTotals?.[p];
                    return (
                      <FromOnePeriodCells
                        key={p}
                        accts={cell?.account_count ?? 0}
                        bal={cell?.total_balance ?? 0}
                      />
                    );
                  })}
                </tr>
              </tbody>
            </table>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );

  // Inline helpers — kept simple to avoid a separate file just for two cells.
  function FromOnePeriodHeaders(): React.JSX.Element {
    return (
      <>
        <th className="py-0.5 px-2 text-right border-l border-slate-200">accts</th>
        <th className="py-0.5 px-2 text-right">balance</th>
      </>
    );
  }
  function FromOnePeriodCells({ accts, bal }: { accts: number; bal: number }): React.JSX.Element {
    return (
      <>
        <td className="py-1 px-2 text-right tabular-nums border-l border-slate-100">{accts}</td>
        <td className="py-1 px-2 text-right tabular-nums">{fmtCurrency(bal)}</td>
      </>
    );
  }
}

function StatusBadge({ status }: { status: 'active' | 'suppressed' }) {
  const cls =
    status === 'active'
      ? 'bg-emerald-100 text-emerald-800'
      : 'bg-slate-200 text-slate-700';
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
}

function SortableTh({
  label,
  field,
  sort,
  dir,
  onSort,
  rowSpan,
}: {
  label: string;
  field: SortKey;
  sort: SortKey;
  dir: 'asc' | 'desc';
  onSort: (k: SortKey) => void;
  rowSpan?: number;
}) {
  const active = sort === field;
  const arrow = active ? (dir === 'asc' ? '▲' : '▼') : '↕';
  return (
    <th rowSpan={rowSpan} className="py-1 pl-1 text-left align-bottom">
      <button
        type="button"
        onClick={() => onSort(field)}
        className={`cursor-pointer select-none uppercase tracking-wide hover:text-slate-900 ${
          active ? 'text-slate-900' : ''
        }`}
      >
        {label}
        <span className={`ml-1 ${active ? 'text-slate-700' : 'text-slate-300'}`}>
          {arrow}
        </span>
      </button>
    </th>
  );
}
